import type { SearchSummary } from '@tunasong/models'
import { dayjs, isScaffold, isTimeStamp, SystemTags } from '@tunasong/models'
import { isProfile } from '@tunasong/schemas'
import {
  SketchBadge,
  TagList,
  TunaBreadcrumbs,
  capitalize,
  Typography,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem,
} from '@tunasong/ui-lib'
import type { MenuItemProps } from '@tunasong/ui-lib'
import type { JSX } from 'react'
import React from 'react'
import { highlightParts } from './highlight.js'
import styles from './search-result-item.module.css'
export interface SearchResultListItemProps extends MenuItemProps {
  className?: string
  icon?: JSX.Element
  element: SearchSummary
  /** Show details */
  showDetails?: boolean
  /** Make the breadcrumbs active links */
  breadCrumbLinks?: boolean
  actions?: React.ReactElement | null
  ref?: React.Ref<HTMLElement>
}

export interface SearchResultContentProps {
  element: SearchSummary
  /** The query, whose words are marked in the name (#2555 M6) */
  highlight?: string
  icon?: JSX.Element
  showDetails?: boolean
  breadCrumbLinks?: boolean
}

/** A result's icon, name and details: in a menu item here, and in Search's listbox options (#2555) */
export const SearchResultContent = (props: SearchResultContentProps) => {
  const { element, showDetails = true, icon, breadCrumbLinks = true, highlight } = props

  // Strip the marker tags from the chip list — the SketchBadge carries the signal instead.
  // 'sys:ai-generated' (canonical), 'sys:scaffold' (status) and the legacy 'tunabrain' tag are
  // all filtered to cover historical data.
  const visibleTags = (element.tags ?? []).filter(
    t => t !== SystemTags.AIGenerated && t !== SystemTags.Scaffold && t !== 'tunabrain'
  )
  /**
   * Status, not provenance: a kept image is the user's and carries no badge in any list. Labelling
   * work the musician decided to keep is what the research says devalues it.
   */
  const showSketchBadge = isScaffold(element)

  const details = element?.type ? (
    <Typography variant="caption">
      <TunaBreadcrumbs
        className={styles.breadcrumbs}
        parentChain={[element, ...(element.parentChain ?? [])].reverse()}
        variant="caption"
        showHome={false}
        showLeaf={false}
        activeLinks={breadCrumbLinks}
      />
      <span className={styles.typeLabel}>{capitalize(element.type)}</span>.{' '}
      {isTimeStamp(element) ? `Updated ${dayjs(element.updatedAt).fromNow()}` : ''}.
      {showSketchBadge ? <SketchBadge size="sm" /> : null}
      <TagList tags={visibleTags} size="small" />
    </Typography>
  ) : null

  const name = isProfile(element) ? (element.name ?? element.email) : element.name
  return (
    <>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText
        className={styles.listItemText}
        primary={
          <Typography className={styles.primaryText}>
            {highlight && name
              ? highlightParts(name, highlight).map((part, index) =>
                  // Parts are positional: the same text may repeat
                  part.match ? <mark key={index}>{part.text}</mark> : <span key={index}>{part.text}</span>
                )
              : name}
          </Typography>
        }
        secondary={showDetails ? details : null}
      />
    </>
  )
}

export const SearchResultListItem = (props: SearchResultListItemProps) => {
  const { element, showDetails = true, actions, icon, breadCrumbLinks = true, ref, ...restProps } = props
  return (
    <MenuItem ref={ref} {...restProps}>
      <SearchResultContent element={element} icon={icon} showDetails={showDetails} breadCrumbLinks={breadCrumbLinks} />
      <ListItemSecondaryAction>{actions}</ListItemSecondaryAction>
    </MenuItem>
  )
}

export default SearchResultListItem
